/**
 * Le système de QR code bancaire de chaque pays — le même tableau que le
 * serveur (backend/.../document/SystemeQr.java).
 *
 * <p>Trois familles : le QR calculé depuis l'IBAN (QR-facture suisse, QR-code
 * européen EPC, QR Platba tchèque, QR code de virement polonais), le QR
 * calculé depuis l'identifiant de paiement du pays (UPI, Pix, PromptPay,
 * PayNow, VietQR, FPS — ces pays n'utilisent pas l'IBAN), et partout ailleurs
 * le QR délivré par la banque, ajouté une fois en image. Sans QR code ajouté,
 * ou avec « IBAN uniquement », la facture porte l'IBAN seul.
 */

/** Les systèmes, avec ce que l'entreprise doit saisir pour les calculer. */
export const SYSTEMES_QR = {
  SUISSE: { nom: 'QR-facture', depuis: 'iban' },
  EPC: { nom: 'QR-code SEPA', depuis: 'iban' },
  QR_PLATBA: { nom: 'QR Platba', depuis: 'iban' },
  ZBP: { nom: 'QR code de virement', depuis: 'iban' },
  UPI: {
    nom: 'UPI',
    depuis: 'identifiant',
    champ: 'Identifiant UPI (VPA)',
    exemple: 'ex. entreprise@okaxis',
  },
  PIX: {
    nom: 'Pix',
    depuis: 'identifiant',
    champ: 'Clé Pix',
    exemple: 'e-mail, téléphone, CPF/CNPJ ou clé aléatoire',
  },
  PROMPTPAY: {
    nom: 'PromptPay',
    depuis: 'identifiant',
    champ: 'Numéro PromptPay',
    exemple: 'téléphone (08…) ou numéro fiscal à 13 chiffres',
  },
  PAYNOW: {
    nom: 'PayNow',
    depuis: 'identifiant',
    champ: 'UEN ou téléphone PayNow',
    exemple: 'ex. 201912345K ou 81234567',
  },
  VIETQR: {
    nom: 'VietQR',
    depuis: 'identifiant',
    champ: 'Numéro de compte',
    exemple: 'ex. 0123456789',
    champBanque: 'Code de la banque (BIN, 6 chiffres)',
    exempleBanque: 'ex. 970436 (Vietcombank)',
  },
  FPS: {
    nom: 'FPS',
    depuis: 'identifiant',
    champ: 'Identifiant FPS, téléphone ou e-mail',
    exemple: 'ex. 1234567',
  },
  BANQUE: { nom: 'QR code de la banque', depuis: 'image' },
};

// Pays de l'euro dans l'espace SEPA (Monaco, Saint-Marin, Andorre et le
// Vatican compris) : le QR-code de virement européen y est lu partout.
const EUROPE = [
  'DE',
  'AT',
  'FR',
  'IT',
  'ES',
  'PT',
  'IE',
  'FI',
  'EE',
  'LV',
  'LT',
  'NL',
  'BE',
  'LU',
  'SK',
  'SI',
  'GR',
  'CY',
  'MT',
  'HR',
  'MC',
  'SM',
  'AD',
  'VA',
];

const PAR_IDENTIFIANT = {
  IN: 'UPI',
  BR: 'PIX',
  TH: 'PROMPTPAY',
  SG: 'PAYNOW',
  VN: 'VIETQR',
  HK: 'FPS',
};

/**
 * @param {string} [pays] code ISO du pays de l'entreprise, la Suisse s'il manque
 * @returns {string|null} le système (clé de {@link SYSTEMES_QR}), null sans QR code
 */
export function systemeQrDuPays(pays) {
  const code = String(pays || 'CH').toUpperCase();
  if (code === 'CH' || code === 'LI') return 'SUISSE';
  if (EUROPE.includes(code)) return 'EPC';
  if (code === 'CZ') return 'QR_PLATBA';
  if (code === 'PL') return 'ZBP';
  if (PAR_IDENTIFIANT[code]) return PAR_IDENTIFIANT[code];
  // Partout ailleurs, la banque peut délivrer son QR code : il figure s'il
  // est ajouté, l'IBAN seul sinon.
  return 'BANQUE';
}

/**
 * Le système dont la pièce porte le QR code, comme le serveur le décide :
 * aucun avec « IBAN uniquement », « Bulletin de ma banque » non lu ou sans compte ; le QR-facture seulement avec
 * un compte suisse ou liechtensteinois ; un QR calculé seulement si
 * l'identifiant est saisi ; celui de la banque seulement si son image est là.
 *
 * @param {object} soc société (pays, iban, paiement, qrId, qrBanque, qrImage)
 * @returns {string|null} le système, null si la pièce n'a pas de QR code
 */
export function systemeQrUtilisable(soc) {
  const iban = String(soc?.iban || '').trim();
  if (!iban || soc?.paiement === 'iban') return null;
  // « Bulletin de ma banque » : son QR code lu, la facture refait le bulletin
  // officiel avec ce compte ; sinon l'image est imprimée telle quelle.
  if (soc?.paiement === 'bulletin' && !soc?.bulletinLu) return null;
  const systeme = systemeQrDuPays(soc?.pays);
  if (!systeme) return null;
  const id = String(soc?.qrId || '').trim();
  switch (systeme) {
    case 'SUISSE':
      return /^(CH|LI)/i.test(iban.replace(/\s/g, '')) ? systeme : null;
    case 'EPC':
      return systeme;
    case 'QR_PLATBA':
      return /^CZ/i.test(iban.replace(/\s/g, '')) ? systeme : null;
    case 'ZBP':
      return /^PL/i.test(iban.replace(/\s/g, '')) ? systeme : null;
    case 'VIETQR':
      return id && String(soc?.qrBanque || '').trim() ? systeme : null;
    case 'BANQUE':
      return String(soc?.qrImage || '').startsWith('data:image/') ? systeme : null;
    default:
      return id ? systeme : null;
  }
}
